import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Send, Loader2, Users, Briefcase, FileText, AlertTriangle, TrendingUp, PoundSterling, Layers, Building2, ArrowRight } from "lucide-react";
import { api } from "@/lib/api";
import { useOrg } from "@/context/OrgContext";
import { PageHeader, Scroller, Spinner } from "@/pages/_shared";
import { BrandMark } from "@/components/BrandLogo";
import { CompanyBadge } from "@/components/CompanyBadge";

export default function BizDashboard() {
  const { current, activeCompany, activeCompanyId, isGroupView, switchCompany } = useOrg();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [group, setGroup] = useState(null);
  const [q, setQ] = useState("");
  const [answer, setAnswer] = useState("");
  const [asking, setAsking] = useState(false);

  useEffect(() => {
    setData(null); setGroup(null); setAnswer("");
    if (isGroupView) {
      api.get("/group/overview").then(({ data }) => setGroup(data)).catch(() => setGroup({ error: true }));
    } else {
      api.get("/dashboard/business").then(({ data }) => setData(data)).catch(() => setData({ error: true }));
    }
    api.get("/coo/brief").then(({ data }) => setAnswer(data.brief || "")).catch(() => {});
  }, [activeCompanyId, isGroupView]);

  const ask = async () => {
    if (!q.trim()) return;
    setAsking(true); setAnswer("");
    try { const { data } = await api.post("/coo/ask", { question: q }); setAnswer(data.answer); }
    catch (e) { setAnswer("BDVG AI is unavailable right now."); }
    finally { setAsking(false); }
  };

  const CooBox = ({ title }) => (
    <div className="mb-6 rounded-2xl border border-gold/20 bg-gradient-to-br from-gold/[0.06] to-transparent p-5">
      <div className="flex items-center gap-2.5"><BrandMark size={28} /><span className="font-heading font-semibold text-gold">{title}</span></div>
      <div className="mt-3 flex gap-2">
        <input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && ask()} data-testid="coo-input"
          placeholder={isGroupView ? "Ask about the whole group…" : `What needs attention at ${activeCompany?.name || "this business"}?`} className="flex-1 rounded-xl border border-white/10 bg-graphite px-4 py-2.5 text-sm focus:border-gold/40 focus:outline-none" />
        <button onClick={ask} disabled={asking} className="flex items-center gap-2 rounded-xl bg-gradient-to-r from-gold-light to-gold px-4 text-sm font-semibold text-black disabled:opacity-40" data-testid="coo-ask">
          {asking ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
        </button>
      </div>
      {answer && <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="mt-4 whitespace-pre-wrap text-sm leading-relaxed text-white/80" data-testid="coo-answer">{answer}</motion.p>}
    </div>
  );

  // ---------------- GROUP / ALL BUSINESSES VIEW ----------------
  if (isGroupView) {
    if (!group) return <Spinner />;
    const t = group.totals || {};
    const cards = [
      { label: "Group Revenue", value: `£${(t.revenue || 0).toLocaleString()}`, icon: PoundSterling },
      { label: "Customers", value: t.customers || 0, icon: Users },
      { label: "Open Jobs", value: t.open_jobs || 0, icon: Briefcase },
      { label: "Overdue", value: t.overdue_invoices || 0, icon: AlertTriangle, danger: (t.overdue_invoices || 0) > 0, sub: `£${t.overdue_total || 0}` },
    ];
    return (
      <Scroller>
        <PageHeader title="Owner Console" subtitle={`${current?.org?.name} · group-wide command centre across ${group.companies_count} businesses`} icon={Layers} />
        <CooBox title="Ask your Group COO" />
        <p className="mb-3 text-xs uppercase tracking-wider text-white/35">Group snapshot</p>
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
          {cards.map((c) => (
            <div key={c.label} className={`rounded-2xl border bg-charcoal p-5 ${c.danger ? "border-red-500/30" : "border-white/8"}`} data-testid={`group-stat-${c.label.toLowerCase().replace(/ /g, "-")}`}>
              <c.icon className={`h-5 w-5 ${c.danger ? "text-red-400" : "text-gold"}`} />
              <p className="mt-3 font-heading text-2xl font-bold">{c.value}</p>
              <p className="text-sm text-white/45">{c.label}{c.sub ? ` · ${c.sub}` : ""}</p>
            </div>
          ))}
        </div>

        {group.alerts?.length > 0 && (
          <div className="mt-5 rounded-2xl border border-red-500/25 bg-red-500/[0.04] p-4" data-testid="group-alerts">
            <p className="mb-2 flex items-center gap-2 text-sm font-medium text-red-300"><AlertTriangle className="h-4 w-4" /> Attention required</p>
            {group.alerts.map((a, i) => (
              <p key={i} className="text-sm text-white/70">{a.company}: {a.count} overdue invoice(s) · £{a.amount}</p>
            ))}
          </div>
        )}

        <p className="mb-3 mt-7 text-xs uppercase tracking-wider text-white/35">Businesses in the group</p>
        <div className="grid gap-3 md:grid-cols-2">
          {group.per_company.map(({ company, snapshot }) => (
            <button key={company.id} onClick={() => { switchCompany(company.id); nav("/app/dashboard"); }}
              className="group flex flex-col rounded-2xl border border-white/8 bg-charcoal p-5 text-left transition-all hover:-translate-y-0.5 hover:border-gold/30" data-testid={`group-company-${company.id}`}>
              <div className="flex items-center justify-between">
                <div className="flex min-w-0 items-center gap-3">
                  <CompanyBadge company={company} size={40} />
                  <div className="min-w-0">
                    <p className="truncate font-heading text-base font-semibold text-white/90">{company.name}</p>
                    <p className="text-xs capitalize text-white/40">{company.industry || "business"}{company.status === "archived" ? " · archived" : ""}</p>
                  </div>
                </div>
                <ArrowRight className="h-4 w-4 text-white/25 transition-colors group-hover:text-gold" />
              </div>
              <div className="mt-4 grid grid-cols-4 gap-2 text-center">
                {[["Revenue", `£${snapshot.revenue}`], ["Customers", snapshot.customers], ["Jobs", snapshot.open_jobs], ["Overdue", snapshot.overdue_invoices]].map(([l, v]) => (
                  <div key={l}><p className="font-heading text-lg font-bold text-white/85">{v}</p><p className="text-[10px] uppercase tracking-wide text-white/35">{l}</p></div>
                ))}
              </div>
            </button>
          ))}
        </div>
      </Scroller>
    );
  }

  // ---------------- SINGLE BUSINESS VIEW ----------------
  if (!data) return <Spinner />;
  const s = data.snapshot;
  const cards = [
    { label: "Customers", value: s.customers, icon: Users },
    { label: "Open Jobs", value: s.open_jobs, icon: Briefcase },
    { label: "Unpaid Invoices", value: s.unpaid_invoices, icon: FileText, sub: `£${s.unpaid_total}` },
    { label: "Overdue", value: s.overdue_invoices, icon: AlertTriangle, danger: s.overdue_invoices > 0, sub: `£${s.overdue_total}` },
  ];

  return (
    <Scroller>
      <div className="mb-6 flex items-center gap-3">
        {activeCompany && <CompanyBadge company={activeCompany} size={44} />}
        <div>
          <h1 className="font-heading text-2xl font-bold tracking-tight">{activeCompany?.name || "Business"}</h1>
          <p className="mt-0.5 text-sm text-white/45">{current?.org?.name} · your BDVG AI workspace</p>
        </div>
      </div>

      <CooBox title="Ask your COO" />

      <p className="mb-3 text-xs uppercase tracking-wider text-white/35">Business snapshot</p>
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        {cards.map((c) => (
          <div key={c.label} className={`rounded-2xl border bg-charcoal p-5 ${c.danger ? "border-red-500/30" : "border-white/8"}`} data-testid={`stat-${c.label.toLowerCase().replace(/ /g,'-')}`}>
            <c.icon className={`h-5 w-5 ${c.danger ? "text-red-400" : "text-gold"}`} />
            <p className="mt-3 font-heading text-3xl font-bold">{c.value}</p>
            <p className="text-sm text-white/45">{c.label}{c.sub ? ` · ${c.sub}` : ""}</p>
          </div>
        ))}
      </div>

      <div className="mt-6 grid gap-6 lg:grid-cols-2">
        <Panel title="Recent Jobs" items={data.recent_jobs} render={(j) => (
          <div key={j.id} className="flex items-center justify-between rounded-lg px-3 py-2 text-sm">
            <span className="truncate text-white/70">{j.title}</span>
            <span className="text-xs capitalize text-gold/70">{j.status}</span>
          </div>)} empty="No jobs yet" />
        <Panel title="Recent Invoices" items={data.recent_invoices} render={(i) => (
          <div key={i.id} className="flex items-center justify-between rounded-lg px-3 py-2 text-sm">
            <span className="truncate text-white/70">{i.number} · {i.customer_name || "—"}</span>
            <span className={`text-xs ${i.status === "paid" ? "text-emerald-400" : "text-gold/70"}`}>£{i.total} · {i.status}</span>
          </div>)} empty="No invoices yet" />
      </div>
    </Scroller>
  );
}

function Panel({ title, items, render, empty }) {
  return (
    <div className="rounded-2xl border border-white/8 bg-charcoal/50 p-4">
      <p className="mb-2 px-1 text-sm font-medium text-white/70">{title}</p>
      {items?.length ? items.map(render) : <p className="px-3 py-4 text-sm text-white/30">{empty}</p>}
    </div>
  );
}
